import React, { useEffect, useState } from "react";

interface AnimatedThreeDotsProps {
    text?: string;
    textClassName?: string;
    style?: React.CSSProperties;
    numDots?: number;
    numToFreeze?: number;
    numIterations?: number;
    typingDelay?: number;
    dotsDelay?: number;
}

export function AnimatedThreeDots({
    text,
    textClassName,
    style,
    numDots = 3,
    numToFreeze = 3,
    numIterations = 2,
    typingDelay = 40,
    dotsDelay = 80,
}: AnimatedThreeDotsProps) {
    const [tick, setTick] = useState(0);

    const textLength = text?.length || 0;
    const dotCycleLength = numDots + numToFreeze;
    const dotsTick = tick > textLength ? tick - textLength : 0;
    const numDotCycles = Math.floor(dotsTick / dotCycleLength);
    const freezeAnimation = numDotCycles >= numIterations;

    useEffect(() => {
        if (freezeAnimation) return;

        const delay = text && tick < textLength ? typingDelay : dotsDelay;
        const timeoutId = setTimeout(() => setTick(prev => prev + 1), delay);
        return () => clearTimeout(timeoutId);
    }, [tick, text, textLength, typingDelay, dotsDelay, freezeAnimation]);

    // Calculate visible dots
    const visibleDotsCount = tick < textLength ? 0 :
        freezeAnimation ? numDots :
        Math.min(numDots, dotsTick % dotCycleLength);

    // Render character with visibility based on tick
    const renderChar = (char: string, idx: number, isVisible: boolean) => (
        <span
            key={idx}
            className={textClassName}
            style={{
                ...style,
                color: isVisible ? "inherit" : "transparent",
                display: char === " " ? undefined : "inline-block",
            }}
        >
            {char}
        </span>
    );

    // Render dots
    const dots = (
        <span aria-hidden="true" style={{ display: "inline-flex" }}>
            {Array.from({ length: numDots }, (_, idx) =>
                renderChar(".", idx, idx < visibleDotsCount)
            )}
        </span>
    );

    // No text - just show dots
    if (!text) return dots;

    const visibleCharsCount = Math.min(textLength, tick);
    const lastSpaceIdx = text.lastIndexOf(" ");
    const beforeLastWord = lastSpaceIdx !== -1 ? text.substring(0, lastSpaceIdx + 1) : "";
    const lastWord = lastSpaceIdx !== -1 ? text.substring(lastSpaceIdx + 1) : text;

    return (
        <span style={style}>
            {beforeLastWord.split("").map((char, idx) =>
                renderChar(char, idx, idx < visibleCharsCount)
            )}
            <span style={{ whiteSpace: "nowrap", display: "inline-block" }}>
                {lastWord.split("").map((char, idx) =>
                    renderChar(char, idx + beforeLastWord.length, idx + beforeLastWord.length < visibleCharsCount)
                )}
                {dots}
            </span>
        </span>
    );
}